//群聊管理
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  TouchableOpacity,
  Switch,
  FlatList,
  StyleSheet,
  ScrollView,
  type TextInputRef,
} from "~/ui/primitives";
import { observer } from "mobx-react-lite";
import { useSafeAreaInsets } from "~/ui/safe-area";
import styled, { useTheme } from "~/ui/styled";
import { Tabs } from "~components/Tabs";
import {
  StartFlex,
  ColumnFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  Button,
  Flex,
} from "~components/El";
import { ChatPerms } from "~proto";
import { useStores } from "~models";
import { useActiveChat } from "~hooks/useActiveChat";
import { MultiLineTextField } from "~components/InputField";
import { DialogShareContent } from "~components/PopupComponents";
import { UploadableAvatar, Avatar } from "~widgets/avatar";
import useGroupActions from "~hooks/useGroupActions";
import { useModal } from "~hooks/useModal";
import { usePopup } from "~hooks/usePopup";
import useNavigation from "~hooks/useNavigation";
import { formatDate, globalToast } from "~utils";
import ChatGroupBotContentModal from "../components/ChatGroupBotContentModal";
import { globalSpinner } from "~utils"
import { flexPageContainer, scrollViewFillStyle } from "~/ui/scroll-layout";

const Container = styled(Flex)`
  ${flexPageContainer}
`;
const TextInput = styled.TextInput`
  padding: 0 10px;
  width: 100%;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(7, 193, 96, 0.15);
  }
`

const ChatPermsLabel: Record<ChatPerms, string> = {
  [ChatPerms.DEFAULT]: "成员",
  [ChatPerms.ADMIN]: "群主",
  [ChatPerms.SUB_ADMIN]: "管理员",
};

const Header = ({ current }) => {
  const theme = useTheme();
  const { updateGroupTitle, updateGroupAvatar } = useGroupActions();
  const [isEditing, setIsEditing] = useState(false);
  const [groupName, setGroupName] = useState(current.title);
  const groupNameInputRef = useRef<TextInputRef>(null);

  useEffect(() => {
    if (!isEditing) return undefined
    const timer = window.setTimeout(() => {
      groupNameInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [isEditing]);

  return (
    <CenterFlex height={"auto"} padding="10px">
      <BetweenFlex
        height={76}
        padding="0 10px"
        gap={theme.space.sm}
        flex={1}
        bg="blockBackground"
        borderRadius="12px"
      >
        <UploadableAvatar
          avatar={current.image}
          name={current.title}
          size={56}
          onChange={(result) => {
            current.setImage(result.uri);
            updateGroupAvatar({
              chat_id: current.chat_id,
              image: result.uri,
              type: current.type,
            });
          }}
        />
        <ColumnFlex minWidth={'max-content'} height={'auto'} flex={1} gap={theme.space.xs}>
          {isEditing ? (
            <TextInput
              ref={groupNameInputRef}
              autoFocus
              placeholder="请输入群名称"
              maxLength={20}
              value={groupName}
              onChangeText={(text) => setGroupName(text)}
            />
          ) : (
            <Text minWidth={'max-content'} color="text" fontWeight="600" fontSize={theme.fontSizes.lg}>
              {groupName}
            </Text>
          )}
          <StartFlex gap={theme.space.xs}>
            <IconFont
              name={"cjql"}
              color={theme.colors.secondaryText}
              size={14}
            />
            <Text color="secondaryText">
              {current?.members?.length || "0"}人
            </Text>
          </StartFlex>
        </ColumnFlex>
        {current?.isCurrentUserIsAdmin && (
          <StartFlex gap={theme.space.xs}>
            {isEditing ? (
              <>
                <TouchableOpacity
                  onClick={() => {
                    updateGroupTitle({
                      chat_id: current.chat_id,
                      title: groupName,
                      type: current.type,
                    });
                    setIsEditing(false);
                  }}
                >
                  <CenterFlex
                    width={30}
                    height={30}
                    borderRadius={15}
                    bg="rgba(7, 193, 96, 0.1)"
                  >
                    <IconFont name={"js"} color={"primary"} size={16} />
                  </CenterFlex>
                </TouchableOpacity>

                <TouchableOpacity
                  onClick={() => {
                    setIsEditing(false);
                  }}
                >
                  <CenterFlex
                    width={30}
                    height={30}
                    borderRadius={15}
                    bg="background"
                  >
                    <IconFont
                      name={"close"}
                      color={"secondaryText"}
                      size={14}
                    />
                  </CenterFlex>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <CenterFlex
                  width={30}
                  height={30}
                  borderRadius={15}
                  bg="background"
                >
                  <IconFont
                    name={"a-Frame1561999600"}
                    color={"primaryText"}
                    size={16}
                  />
                </CenterFlex>
              </TouchableOpacity>
            )}
          </StartFlex>
        )}
      </BetweenFlex>
    </CenterFlex>
  );
};

const ActionItem: React.FC<any> = ({ actionsItem }) => {
  return (
    <TouchableOpacity
      onClick={() => {
        actionsItem.handleClick && actionsItem.handleClick();
      }}
    >
      <CenterFlex
        width={28}
        height={28}
        borderRadius={14}
        bg={actionsItem.iconBgColor}
      >
        <IconFont
          name={actionsItem.icon}
          color={actionsItem.iconColor}
          size={14}
        />
      </CenterFlex>
    </TouchableOpacity>
  );
};

const GroupMembersItem = observer(
  ({ item, index }: { item: any; index: number }) => {
    // console.log("item", item)
    const theme = useTheme();
    const navigation = useNavigation();
    const { chat: current } = useActiveChat();
    const { Dialog } = useModal();
    const popup = usePopup();
    const { setGroupMemberRole, handleRemoveGroupMember } = useGroupActions();

    if (!current) return null;

    const canManageMembers =
      current.isCurrentUserIsAdmin || current.isCurrentUserIsManager;
    const canOperateOnTarget = current.isCurrentUserIsAdmin
      ? item.perm !== ChatPerms.ADMIN
      : item.perm === ChatPerms.DEFAULT;

    const actions = [
      // {
      //     title: "用户禁言",
      //     icon: index % 2 === 1 ? "a-Frame1561999595" : "Union",
      //     iconColor: index % 2 === 1 ? "#07c160" : "#fa5251",
      //     iconBgColor: index % 2 === 1 ? "rgba(7, 193, 96, 0.1)" : "#fee2e1",
      //     handleClick: () => {
      //       setMessageModal({
      //         visible: true,
      //         extra: {
      //           message: "测试1",
      //           onConfirm: () => {
      //           },
      //           onCancel: () => {
      //           },
      //         },
      //       })
      //     },
      //   },
      // Only group owner can appoint/cancel admins.
      ...(!current.isCurrentUserIsAdmin
        ? []
        : [
            {
              title: "管理员",
              icon: "a-Frame1561999596",
              iconColor:
                item.perm === ChatPerms.SUB_ADMIN ? "#07c160" : "#a2a8af",
              iconBgColor:
                item.perm === ChatPerms.SUB_ADMIN
                  ? "rgba(7, 193, 96, 0.1)"
                  : "#f7f7f7",
              handleClick: () => {
                Dialog.show({
                  title: "温馨提示",
                  content: React.createElement(DialogShareContent, {
                    content: `确定要${
                      item.perm === ChatPerms.SUB_ADMIN ? "取消" : "任命"
                    } ${item.user?.name} 为管理员吗？`,
                    subContent: item.user?.name,
                    avatarProps: {
                      size: 36,
                      name: item.user?.name,
                      avatar: item.user?.avatar,
                    },
                  }),
                  onConfirm: () => {
                    setGroupMemberRole(
                      {
                        user_id: item.user_id,
                        is_sub_admin:
                          item.perm === ChatPerms.SUB_ADMIN ? false : true,
                      },
                      (data) => {
                        globalToast.show(
                          `${
                            item.perm === ChatPerms.SUB_ADMIN ? "任命" : "取消"
                          }管理员成功`,
                        );
                      },
                      (error) => {},
                    );
                  },
                });
              },
            },
          ]),
    ];
    if (current.isGameGroup && current.isCurrentUserIsAdmin) {
      // 游戏群可以设置群代理、赔付号机器人、领红包机器人
      actions.push({
        title: "群代理",
        icon: "Frame",
        iconColor: item.is_agent ? "#07c160" : "#a2a8af",
        iconBgColor: item.is_agent ? "rgba(7, 193, 96, 0.1)" : "#f7f7f7",
        handleClick: () => {
          Dialog.show({
            title: "温馨提示",
            content: React.createElement(DialogShareContent, {
              content: `确定要${item.is_agent ? "取消" : "设置"} ${
                item.user?.name
              } 为群代理吗？`,
              subContent: item.user?.name,
              avatarProps: {
                size: 36,
                name: item.user?.name,
                avatar: item.user?.avatar,
              },
            }),
            onConfirm: () => {
              setGroupMemberRole(
                {
                  user_id: item.user_id,
                  is_agent: !item.is_agent,
                },
                (data) => {},
                (error) => {},
              );
            },
          });
        },
      });

      actions.push({
        title: "赔付号机器人",
        icon: "a-Frame1561999597",
        iconColor: item.is_compensate_bot ? "#07c160" : "#a2a8af",
        iconBgColor: item.is_compensate_bot
          ? "rgba(7, 193, 96, 0.1)"
          : "#f7f7f7",
        handleClick: () => {
          setGroupMemberRole(
            {
              user_id: item.user_id,
              is_compensate_bot: item.is_compensate_bot ? false : true,
            },
            (data) => {
              Dialog.show({
                title: "温馨提示",
                closeOnAction: false,
                content: React.createElement(DialogShareContent, {
                  content: `已${item.is_compensate_bot ? "设置" : "取消"} ${
                    item.user?.name
                  } 为赔付号机器人`,
                  subContent: item.user?.name,
                  avatarProps: {
                    size: 36,
                    name: item.user?.name,
                    avatar: item.user?.avatar,
                  },
                }),
                onConfirm: () => {},
              });
            },
            (error) => {
              globalToast.show(error.message || "设置赔付号机器人失败");
            },
          );
        },
      });

      actions.push({
        title: "领红包机器人",
        icon: "Frame-1",
        iconColor: item.is_collect_bot ? "#07c160" : "#a2a8af",
        iconBgColor: item.is_collect_bot ? "rgba(7, 193, 96, 0.1)" : "#f7f7f7",
        handleClick: async () => {
          if (item.is_collect_bot) {
            // 取消
            setGroupMemberRole(
              {
                user_id: item.user_id,
                is_collect_bot: false,
              },
              (data) => {
                Dialog.show({
                  title: "温馨提示",
                  closeOnAction: false,
                  content: React.createElement(DialogShareContent, {
                    content: `已${item.is_collect_bot ? "设置" : "取消"} ${
                      item.user?.name
                    } 为领红包机器人`,
                    subContent: item.user?.name,
                    avatarProps: {
                      size: 36,
                      name: item.user?.name,
                      avatar: item.user?.avatar,
                    },
                  }),
                  onConfirm: () => {},
                });
              },
              (error) => {
                globalToast.show(error.message || "取消领红包机器人失败");
              },
            );
          } else {
            // 添加
            const p = popup.open({
              type: "center",
              content: React.createElement(ChatGroupBotContentModal, {
                onConfirm: (val) => {
                  popup.close(p.id);
                  const params: {
                    user_id: number | string;
                    is_collect_bot_dragon?: boolean;
                    is_collect_bot_tiger?: boolean;
                  } = {
                    user_id: item.user_id,
                  };
                  if (val === "tiger") {
                    params.is_collect_bot_tiger = true;
                  } else if (val === "dragon") {
                    params.is_collect_bot_dragon = true;
                  }
                  setGroupMemberRole(
                    params,
                    (data) => {
                      Dialog.show({
                        title: "温馨提示",
                        closeOnAction: false,
                        content: React.createElement(DialogShareContent, {
                          content: `已${
                            item.is_collect_bot ? "设置" : "取消"
                          } ${item.user?.name} 为领红包机器人`,
                          subContent: item.user?.name,
                          avatarProps: {
                            size: 36,
                            name: item.user?.name,
                            avatar: item.user?.avatar,
                          },
                        }),
                        onConfirm: () => {},
                      });
                    },
                    (error) => {
                      globalToast.show(error.message || "设置领红包机器人失败");
                    },
                  );
                },
                onClose: () => {
                  popup.close(p.id);
                },
              }),
            });
          }
        },
      });
      actions.push({
        title: "设置自动发红包下注",
        icon: "zjhy",
        iconColor: "#a2a8af",
        iconBgColor: "#f7f7f7",
        handleClick: () => {
          navigation.navigate("AutoRedPacketBetSetting", {
            chat_id: current.id,
            user_id: item.user_id,
          });
        },
      });
    }
    actions.push({
      title: "移除群成员",
      icon: "a-Frame1561999598",
      iconColor: "#a2a8af",
      iconBgColor: "#f7f7f7",
      handleClick: () => {
        Dialog.show({
          title: "温馨提示",
          content: React.createElement(DialogShareContent, {
            content: `确定要移除 ${item.user?.name} 出群吗？`,
            subContent: item.user?.name,
            avatarProps: {
              size: 36,
              name: item.user?.name,
              avatar: item.user?.avatar,
            },
          }),
          onConfirm: () => {
            handleRemoveGroupMember({ user_ids: [item.user_id] });
          },
        });
      },
    });
    return (
      <BetweenFlex height="auto" gap="10px" width="100%" padding="0 10px" mb="10px">
        <Avatar avatar={item.user?.avatar} size={40} name={item.user?.name} />
        <ColumnFlex flex={1} gap="6px">
          <BetweenFlex width="100%" height="auto" gap="10px" padding="0 0 10px" flex={1} style={{ minWidth: 0 }}>
            <ColumnFlex flex={1} minWidth={0} width="auto" gap="2px">
              <StartFlex height="auto" minWidth={0} width="100%" gap="3px">
                <Text
                  color="text"
                  fontWeight="600"
                  fontSize={theme.fontSizes.lg}
                >
                  {item.user?.name || ""}
                </Text>
                {item.perm === ChatPerms.ADMIN && (
                  <IconFont
                    name="a-Frame1561999599"
                    color="#ff9a35"
                    size={12}
                  />
                )}
                {item.perm === ChatPerms.SUB_ADMIN && (
                  <IconFont
                    name="a-Frame1561999596"
                    color="#ff9a35"
                    size={12}
                  />
                )}
                {item.is_agent && (
                  <IconFont name="Frame" color="#07c160" size={12} />
                )}
                {item.is_compensate_bot && (
                  <IconFont
                    name="a-Frame1561999597"
                    color="#07c160"
                    size={12}
                  />
                )}
                {item.is_collect_bot && (
                  <IconFont name="Frame-1" color="#fa5251" size={12} />
                )}
              </StartFlex>
              <Text color="secondaryText" fontSize={theme.fontSizes.sm}>
                {ChatPermsLabel[item.perm]}
              </Text>
              <Text color="secondaryText" fontSize={theme.fontSizes.sm}>
                加入时间 {formatDate(item.created)}
              </Text>
            </ColumnFlex>
            {canManageMembers && canOperateOnTarget && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
                contentContainerStyle={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                {actions.map((actionsItem, i) => (
                  <ActionItem key={i} actionsItem={actionsItem} item={item} />
                ))}
              </ScrollView>
            )}
          </BetweenFlex>
          <CenterFlex
            height={StyleSheet.hairlineWidth}
            width="100%"
            bg={theme.colors.border}
          />
        </ColumnFlex>
      </BetweenFlex>
    );
  },
);
const GroupMembers = () => {
  const insets = useSafeAreaInsets();
  const { chat: current } = useActiveChat();
  const keyExtractor = useCallback(
    (item, idx) => item.user_id || idx.toString(),
    [],
  );
  const renderItem = useCallback(
    ({ item, index }) => <GroupMembersItem item={item} index={index} />,
    [],
  );
  if (!current) return null;
  const members = (current.members || []).slice().sort((a, b) => {
    // 按照 群主 → 管理员 → 普通成员 排序
    const order = {
      [ChatPerms.ADMIN]: 0,
      [ChatPerms.SUB_ADMIN]: 1,
      [ChatPerms.DEFAULT]: 2,
    };
    return order[a.perm] - order[b.perm];
  });
  return (
    <>
      <FlatList
        data={members}
        keyExtractor={keyExtractor}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: insets.bottom + 60,
        }}
        renderItem={renderItem}
        removeClippedSubviews
        initialNumToRender={12}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        windowSize={10}
        style={{ flex: 1 }}
      />
    </>
  );
};

const GroupSettings = ({ settingData, onRefresh }) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { apiStore } = useStores();
  const { chat: current } = useActiveChat();
  // console.log(current, "current")
  const { handleChatAnnouncement } = useGroupActions();
  const [loading, setLoading] = useState(false);
  const [isEditAnnouncement, setIsEditAnnouncement] = useState(false); // 是否编辑公告
  const [allowGame, setAllowGame] = useState(settingData?.allow_game || false); // 是否允许游戏
  const [allowNewMemberViewHistory, setAllowNewMemberViewHistory] = useState(
    settingData?.allow_history_msg || false,
  ); // 新成员是否允许查看历史
  const [inviteApprovalRequired, setInviteApprovalRequired] = useState(
    settingData?.require_invite_confirm || false,
  ); // 群聊邀请确认
  const [allowAddFriend, setAllowAddFriend] = useState(
    settingData?.forbid_add_friend || false,
  ); // 是否允许添加好友
  const [hideMemberIdentity, setHideMemberIdentity] = useState(
    settingData?.hide_group_info || false,
  ); // 隐藏群成员身份
  const [muteAll, setMuteAll] = useState(settingData?.all_mute || false); // 群消息免打扰
  const [announcementInput, setAnnouncementInput] = useState(
    current?.desc || "",
  );

  useEffect(() => {
    // When switching chats or after store hydration, keep input in sync.
    setAnnouncementInput(current?.desc || "");
  }, [current?.desc]);

  const handleValueChange = useCallback((key, value) => {
    globalSpinner.show();
    switch (key) {
      case "allowGame":
        apiStore
          .updateChannelGroupSetting({ chat_id: current.id, allow_game: value })
          .then((res) => {
            setAllowGame(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
      case "allowNewMemberViewHistory":
        apiStore
          .updateChannelGroupSetting({
            chat_id: current.id,
            allow_history_msg: value,
          })
          .then((res) => {
            setAllowNewMemberViewHistory(value);
            current?.setAllowHistoryMsg?.(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
      case "inviteApprovalRequired":
        apiStore
          .updateChannelGroupSetting({
            chat_id: current.id,
            require_invite_confirm: value,
          })
          .then((res) => {
            setInviteApprovalRequired(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
      case "allowAddFriend":
        apiStore
          .updateChannelGroupSetting({
            chat_id: current.id,
            forbid_add_friend: value,
          })
          .then((res) => {
            setAllowAddFriend(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
      case "hideMemberIdentity":
        apiStore
          .updateChannelGroupSetting({
            chat_id: current.id,
            hide_group_info: value,
          })
          .then((res) => {
            setHideMemberIdentity(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
      case "muteAll":
        apiStore
          .updateChannelGroupSetting({ chat_id: current.id, all_mute: value })
          .then((res) => {
            setMuteAll(value);
            current?.setAllMute?.(value);
            onRefresh();
            globalSpinner.close();
          })
          .catch((error) => {
            globalSpinner.close();
            globalToast.show(error.message || "更新群设置失败");
          });
        break;
    }
  }, []);
  // 获取当前群代理
  const getCurrentGroupAgent = useMemo(() => {
    return (current?.members || []).filter((member) => member.is_agent);
  }, [current?.members]);
  if (!current) return null;
  return (
    <ScrollView
      style={scrollViewFillStyle}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{
        paddingBottom: insets.bottom + 100,
      }}
    >
      <ColumnFlex height={"auto"} width="100%" padding="0 10px" gap="12px">
        <BetweenFlex width="100%" height="42px">
          <StartFlex height={"auto"} gap="10px">
            <IconFont name="cjql" color={"primary"} size={20} />
            <Text color="text" fontWeight="600" fontSize={theme.fontSizes.lg}>
              群公告
            </Text>
          </StartFlex>
          {current.isCurrentUserIsAdmin && (
            <Button
              onClick={() => {
                setAnnouncementInput(current?.desc || "");
                setIsEditAnnouncement(!isEditAnnouncement);
              }}
              padding="0 10px"
              height="24px"
              bg={isEditAnnouncement ? "#f7f7f7" : "rgba(78, 152, 243, 0.1)"}
              borderRadius="4px"
            >
              <Text
                color={isEditAnnouncement ? "#a2a8af" : "#4e98f3"}
                fontSize="12px"
              >
                {isEditAnnouncement ? "取消" : "编辑"}
              </Text>
            </Button>
          )}
        </BetweenFlex>
        {isEditAnnouncement ? (
          <>
            <MultiLineTextField
              value={announcementInput}
              placeholder="请输入群公告"
              maxLength={500}
              style={{ height: 160, paddingBottom: 24 }}
              onChangeText={(value) => {
                setAnnouncementInput(value);
              }}
            />
            <Button
              width="100%"
              bg={theme.colors.buttonBackground}
              borderRadius="10px"
              height="44px"
              loading={loading}
              onClick={() => {
                setLoading(true);
                handleChatAnnouncement({
                  chat_id: current.chat_id,
                  desc: announcementInput,
                })
                  .then((res) => {
                    globalToast.show("群公告保存成功");
                    current?.setAnnouncement?.(announcementInput);
                    setLoading(false);
                    setIsEditAnnouncement(!isEditAnnouncement);
                  })
                  .catch((error) => {
                    globalToast.show(error.message || "群公告保存失败");
                    setLoading(false);
                  });
              }}
            >
              <Text color="#fff" fontSize="14px" fontWeight="600">
                保存
              </Text>
            </Button>
          </>
        ) : (
          <StartFlex
            height={"auto"}
            width="100%"
            padding="13px 12px"
            borderRadius="8px"
            bg="background"
          >
            <Text
              color={
                current?.desc || ""
                  ? theme.colors.text
                  : theme.colors.secondaryText
              }
            >
              {current?.desc || "暂无公告"}
            </Text>
          </StartFlex>
        )}
        {current.isCurrentUserIsAdmin && current.isGameGroup && (
          <ColumnFlex height={"auto"} width="100%">
            <StartFlex gap="10px" height="42px">
              <IconFont name="sz1" color={"primary"} size={20} />
              <Text color="text" fontWeight="600" fontSize="14px">
                群反佣
              </Text>
            </StartFlex>
            <ColumnFlex
              height={"auto"}
              width="100%"
              mt="10px"
              padding="10px"
              borderRadius="8px"
              bg={theme.colors.background}
            >
              <ColumnFlex height={"auto"} width="100%">
                <Text color={theme.colors.text} fontSize="14px">
                  成员反水比例
                </Text>
                <Text color={theme.colors.primary} fontSize="14px" mt="3px">
                  {settingData?.member_rebate_ratio || 0}%
                </Text>
                <Text color={theme.colors.secondary} fontSize="12px" mt="3px">
                  输赢都反
                </Text>
              </ColumnFlex>
            </ColumnFlex>
            <ColumnFlex
              height={"auto"}
              width="100%"
              mt="10px"
              padding="10px"
              borderRadius="8px"
              bg={theme.colors.background}
            >
              <ColumnFlex height={"auto"} width="100%">
                <Text color={theme.colors.text} fontSize="14px">
                  日反亏损比例
                </Text>
                <Text color={theme.colors.error} fontSize="14px" mt="3px">
                  {settingData?.member_daily_loss_rebate_ratio || 0}%
                </Text>
                <Text color={theme.colors.secondary} fontSize="12px" mt="3px">
                  每日亏损反水
                </Text>
              </ColumnFlex>
            </ColumnFlex>
            <ColumnFlex
              height={"auto"}
              width="100%"
              mt="10px"
              padding="10px"
              borderRadius="8px"
              bg={theme.colors.background}
            >
              <ColumnFlex height={"auto"} width="100%">
                <Text color={theme.colors.text} fontSize="14px">
                  代理奖励比例
                </Text>
                <Text
                  color={theme.colors.purpleBackground}
                  fontSize="14px"
                  mt="3px"
                >
                  {settingData?.agent_rebate_ratio || 0}%
                </Text>
                <Text color={theme.colors.secondary} fontSize="12px" mt="3px">
                  下级投注奖励
                </Text>
              </ColumnFlex>
            </ColumnFlex>
            <BetweenFlex
              height={"auto"}
              width="100%"
              mt="10px"
              padding="10px"
              borderRadius="8px"
              bg={theme.colors.background}
            >
              <Text color={theme.colors.text} fontSize="14px">
                当前群代理
              </Text>
              <Text
                color={theme.colors.primaryText}
                fontSize="14px"
                fontWeight="600"
              >
                {getCurrentGroupAgent.length}人
              </Text>
            </BetweenFlex>
          </ColumnFlex>
        )}

        <ColumnFlex width="100%" pr="12px">
          <StartFlex gap="10px" height="42px">
            <IconFont name="sz1" color={"primary"} size={20} />
            <Text color="text" fontWeight="600" fontSize="14px">
              群设置
            </Text>
          </StartFlex>
          {current.isGameGroup && (
            <BetweenFlex width="100%" padding="4px 0">
              <Text color="text">允许游戏功能</Text>
              <Switch
                value={allowGame}
                onValueChange={(val) => handleValueChange("allowGame", val)}
              />
            </BetweenFlex>
          )}

          {/* <BetweenFlex width="100%" padding="4px 0">
            <Text color="text">新成员查看历史消息</Text>
            <Switch value={allowNewMemberViewHistory} onValueChange={(val) => handleValueChange("allowNewMemberViewHistory", val)} />
          </BetweenFlex> */}
          <BetweenFlex width="100%" padding="4px 0">
            <Text color="text">群聊邀请确认</Text>
            <Switch
              value={inviteApprovalRequired}
              onValueChange={(val) =>
                handleValueChange("inviteApprovalRequired", val)
              }
            />
          </BetweenFlex>
          <BetweenFlex width="100%" padding="4px 0">
            <Text color="text">禁止添加好友</Text>
            <Switch
              value={allowAddFriend}
              onValueChange={(val) => handleValueChange("allowAddFriend", val)}
            />
          </BetweenFlex>
          <BetweenFlex width="100%" padding="4px 0">
            <Text color="text">隐藏群成员ID</Text>
            <Switch
              value={hideMemberIdentity}
              onValueChange={(val) =>
                handleValueChange("hideMemberIdentity", val)
              }
            />
          </BetweenFlex>
          <BetweenFlex width="100%" padding="4px 0">
            <Text color="text">全群禁言</Text>
            <Switch
              value={muteAll}
              onValueChange={(val) => handleValueChange("muteAll", val)}
            />
          </BetweenFlex>
        </ColumnFlex>
      </ColumnFlex>
    </ScrollView>
  );
};

const GroupMembersObserver = observer(GroupMembers);
const GroupSettingsObserver = observer(GroupSettings);
const TAB_MAP = {
  群成员: GroupMembersObserver,
  群设置: GroupSettingsObserver,
};
const tabs: string[] = ["群成员", "群设置"];
const GroupChatManagement: React.FC<any> = ({ route }) => {
  const { apiStore } = useStores();
  const { chat: current } = useActiveChat();
  const [tab, setTab] = useState("群成员");
  const [settingData, setSettingData] = useState(null);
  const Com = TAB_MAP[tab];
  const onTabPress = (item: string) => {
    setTab(item);
  };

  // 获取群组设置
  const getSettingInfo = useCallback(async () => {
    try {
      const res = await apiStore.getChannelGroupSettingInfo({
        chat_id: current.id,
        type: 1,
      });
      if (res?.data) {
        setSettingData(res.data);
        if (typeof res.data?.all_mute === "boolean")
          current?.setAllMute?.(res.data.all_mute);
        if (typeof res.data?.allow_history_msg === "boolean")
          current?.setAllowHistoryMsg?.(res.data.allow_history_msg);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    getSettingInfo();
  }, []);

  if (!current) return null;

  return (
    <>
      <Container>
        <Header current={current} />
        <ColumnFlex
          flex={1}
          minHeight={0}
          padding="10px 0"
          borderRadius="12px 12px 0 0"
          bg="blockBackground"
          gap="10px"
          overflow="hidden"
        >
          <CenterFlex height={"auto"} padding="0 10px">
            <Tabs
              height={36}
              tabs={tabs}
              active={tab}
              onChange={(item) =>
                onTabPress(typeof item === "string" ? item : item.value)
              }
            />
          </CenterFlex>
          <Com settingData={settingData} onRefresh={getSettingInfo} />
        </ColumnFlex>
      </Container>
    </>
  );
};

export default observer(GroupChatManagement);
